import type { Candle } from '@/lib/types'

/**
 * Indicateurs superposés au graphique : moyennes mobiles et bandes de
 * Bollinger.
 *
 * Ils sont calculés **dans le navigateur, à partir des bougies déjà
 * chargées**. Aucun aller-retour avec le serveur : une moyenne mobile est une
 * moyenne, le courtier n'a rien à en dire, et la recalculer sur place évite
 * d'attendre le réseau à chaque changement de période.
 *
 * Ce calcul est en virgule flottante, et c'est assumé : un indicateur sert à
 * regarder, jamais à décider d'une taille de position. Tout ce qui touche à
 * l'argent — pip, risque, volume — reste en décimal exact, côté serveur.
 */

export type IndicatorKind = 'sma' | 'ema' | 'bollinger'

export interface Indicator {
  id: string
  kind: IndicatorKind
  /** Nombre de bougies prises en compte. */
  period: number
  /**
   * Nombre d'écarts-types séparant les bandes de la moyenne.
   *
   * N'a de sens que pour Bollinger. Conservé pour tous les indicateurs afin
   * que changer de type dans le panneau ne perde pas le réglage.
   */
  deviations: number
  color: string
  width: number
}

/** Une courbe à tracer : ce que le graphique consomme réellement. */
export interface IndicatorLine {
  /** Identité stable de la courbe, pour la réutiliser d'un rendu à l'autre. */
  key: string
  color: string
  width: number
  dashed: boolean
  points: { time: number; value: number }[]
}

export const INDICATOR_LABELS: Record<IndicatorKind, string> = {
  sma: 'MM',
  ema: 'MME',
  bollinger: 'Bollinger',
}

export const INDICATOR_NAMES: Record<IndicatorKind, string> = {
  sma: 'Moyenne mobile simple',
  ema: 'Moyenne mobile exponentielle',
  bollinger: 'Bandes de Bollinger',
}

/** Palette : les mêmes teintes que les tracés, pour un graphique cohérent. */
export const INDICATOR_COLORS = [
  '#f5a623', // orange
  '#2962ff', // bleu
  '#ab47bc', // violet
  '#26a69a', // vert
  '#ef5350', // rouge
  '#d1d4dc', // gris clair
] as const

/** Bornes de saisie. Le serveur n'a pas son mot à dire : rien n'est envoyé. */
export const MIN_PERIOD = 2
export const MAX_PERIOD = 400
export const MIN_DEVIATIONS = 0.1
export const MAX_DEVIATIONS = 5

const KEY = 'reversetrader.indicators'

/**
 * Les indicateurs sont GLOBAUX, contrairement aux tracés.
 *
 * Une résistance appartient à une paire ; une moyenne mobile à 200 appartient
 * à une façon de travailler. Devoir la reposer sur chaque paire et sur chaque
 * panneau serait le contraire d'un réglage.
 */
export function loadIndicators(): Indicator[] {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as Indicator[]).map(sanitise) : []
  } catch {
    // Un stockage illisible — quota, mode privé, données corrompues — ne doit
    // pas empêcher le graphique de s'afficher.
    return []
  }
}

export function saveIndicators(indicators: Indicator[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(indicators))
  } catch {
    /* Mieux vaut perdre un réglage d'affichage qu'une session. */
  }
}

/**
 * Ramène un indicateur dans ses bornes.
 *
 * Appliqué à la LECTURE et non seulement à la saisie : une valeur relue d'un
 * stockage écrit par une version antérieure — ou trafiqué à la main — ne doit
 * pas pouvoir faire boucler le calcul ni sortir une courbe absurde.
 */
function sanitise(indicator: Indicator): Indicator {
  const period = Math.round(Number(indicator.period))
  const deviations = Number(indicator.deviations)
  return {
    ...indicator,
    kind: indicator.kind in INDICATOR_LABELS ? indicator.kind : 'sma',
    period: Number.isFinite(period) ? clamp(period, MIN_PERIOD, MAX_PERIOD) : 20,
    deviations: Number.isFinite(deviations)
      ? clamp(deviations, MIN_DEVIATIONS, MAX_DEVIATIONS)
      : 2,
    width: clamp(Math.round(Number(indicator.width)) || 2, 1, 4),
  }
}

export function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high)
}

export function newIndicator(kind: IndicatorKind, used: number): Indicator {
  return {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    // Des valeurs que tout le monde reconnaît : 20 pour Bollinger, 50 pour
    // une moyenne. Personne ne veut composer un réglage avant de voir la
    // première courbe.
    period: kind === 'bollinger' ? 20 : 50,
    deviations: 2,
    // On avance dans la palette plutôt que de reprendre la même teinte : deux
    // moyennes de la même couleur ne s'expliquent pas.
    color: INDICATOR_COLORS[used % INDICATOR_COLORS.length],
    width: 2,
  }
}

/** Libellé court affiché dans la liste : « MM 50 », « Bollinger 20 · 2σ ». */
export function describe(indicator: Indicator): string {
  const base = `${INDICATOR_LABELS[indicator.kind]} ${indicator.period}`
  return indicator.kind === 'bollinger' ? `${base} · ${indicator.deviations}σ` : base
}

/* --------------------------------------------------------------------------
   Calcul
-------------------------------------------------------------------------- */

/** Moyenne mobile simple : la moyenne des `period` dernières clôtures. */
function sma(values: number[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(values.length).fill(null)
  let sum = 0
  for (let i = 0; i < values.length; i += 1) {
    sum += values[i]
    if (i >= period) sum -= values[i - period]
    if (i >= period - 1) out[i] = sum / period
  }
  return out
}

/**
 * Moyenne mobile exponentielle.
 *
 * Amorcée sur la moyenne simple des `period` premières valeurs, comme le font
 * les plateformes usuelles. Démarrer sur la première clôture donnerait une
 * courbe visiblement différente les premières dizaines de bougies, et donc
 * des croisements qui n'existent nulle part ailleurs.
 */
function ema(values: number[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(values.length).fill(null)
  if (values.length < period) return out

  const weight = 2 / (period + 1)
  let previous = 0
  for (let i = 0; i < period; i += 1) previous += values[i]
  previous /= period
  out[period - 1] = previous

  for (let i = period; i < values.length; i += 1) {
    previous = values[i] * weight + previous * (1 - weight)
    out[i] = previous
  }
  return out
}

/**
 * Écart-type glissant, de population.
 *
 * C'est la convention de Bollinger — diviser par `n`, pas par `n − 1`. Sur
 * une période de 20 l'écart entre les deux dépasse 2 %, assez pour décaler
 * visiblement les bandes par rapport à toute autre plateforme.
 */
function rollingDeviation(values: number[], period: number, means: (number | null)[]) {
  const out: (number | null)[] = new Array(values.length).fill(null)
  for (let i = period - 1; i < values.length; i += 1) {
    const mean = means[i]
    if (mean === null) continue
    let total = 0
    for (let j = i - period + 1; j <= i; j += 1) {
      const gap = values[j] - mean
      total += gap * gap
    }
    out[i] = Math.sqrt(total / period)
  }
  return out
}

/**
 * Traduit un indicateur en courbes prêtes à tracer.
 *
 * Renvoie une liste vide plutôt qu'une courbe tronquée quand les bougies
 * manquent : une moyenne à 200 sur 50 bougies n'est pas « approximative »,
 * elle n'existe pas.
 */
export function computeLines(indicator: Indicator, candles: Candle[]): IndicatorLine[] {
  if (candles.length < indicator.period) return []

  const closes = candles.map((candle) => Number(candle.close))
  const times = candles.map((candle) => candle.time)

  const line = (
    suffix: string,
    values: (number | null)[],
    options: { dashed?: boolean; width?: number } = {},
  ): IndicatorLine => ({
    key: `${indicator.id}:${suffix}`,
    color: indicator.color,
    width: options.width ?? indicator.width,
    dashed: options.dashed ?? false,
    points: values
      .map((value, index) => ({ time: times[index], value }))
      .filter((point): point is { time: number; value: number } => point.value !== null),
  })

  if (indicator.kind === 'sma') return [line('sma', sma(closes, indicator.period))]
  if (indicator.kind === 'ema') return [line('ema', ema(closes, indicator.period))]

  const middle = sma(closes, indicator.period)
  const deviation = rollingDeviation(closes, indicator.period, middle)
  const band = (sign: 1 | -1) =>
    middle.map((mean, index) => {
      const spread = deviation[index]
      return mean === null || spread === null
        ? null
        : mean + sign * indicator.deviations * spread
    })

  return [
    line('upper', band(1)),
    // La médiane en pointillé et plus fine : c'est une moyenne mobile, pas une
    // borne. La confondre avec une bande fait lire trois niveaux de même
    // nature là où il n'y en a que deux.
    line('middle', middle, { dashed: true, width: 1 }),
    line('lower', band(-1)),
  ]
}

/* --------------------------------------------------------------------------
   Partage entre graphiques

   Les indicateurs sont globaux, et plusieurs graphiques coexistent : la
   grille de bureau en affiche jusqu'à quatre, et l'éditeur de position ouvre
   le sien. Si chacun tenait sa propre copie, ajouter une moyenne sur l'un
   laisserait les autres sans rien jusqu'au prochain rechargement — le
   contraire d'un réglage global.

   D'où ce petit dépôt partagé : une seule liste en mémoire, et des abonnés
   prévenus ensemble. La lecture rend TOUJOURS la même référence tant que rien
   n'a changé, condition nécessaire au branchement React.
-------------------------------------------------------------------------- */

let current: Indicator[] | null = null
const listeners = new Set<() => void>()

/** Liste courante, chargée du stockage au premier appel. */
export function indicatorsSnapshot(): Indicator[] {
  if (current === null) current = loadIndicators()
  return current
}

export function setIndicators(next: Indicator[]): void {
  current = next.map(sanitise)
  saveIndicators(current)
  for (const listener of listeners) listener()
}

export function subscribeIndicators(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
